import { formatNumber } from '@angular/common';
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { LayoutService } from '../../../../RefModules/_metronic/core';
import { HelperService } from '../../../../services/helper.service';
import { MeasurementService } from '../../../../services/measurement.service';
import { CalculationResult } from '../../models/calculationModels/calculation-result';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { takeUntil } from 'rxjs/operators';

interface keyvaluePairs {
    key: string;
    value: string;
}

@Component({
    selector: 'app-advanced-output',
    templateUrl: './advanced-output.component.html',
    styleUrls: ['./advanced-output.component.scss'],
    standalone: false
})
export class AdvancedOutputComponent implements OnInit, OnDestroy {
    @Input() showpaneltoggle;
    @Input()
    set calcOutput(value: CalculationResult) {
        this._calcOutput = value;
        if (value !== undefined && value?.output !== null) {
            this.loadAdvancedOutputLists();
        }
    }
    get calcOutput(): CalculationResult {
        return this._calcOutput;
    }
    @Output() hidepanel = new EventEmitter();
    destroy$ = new Subject();

    extrasQuickPanelOffcanvasDirectionCSSClass = 'offcanvas-right';
    boltList: keyvaluePairs[];
    flangeList: keyvaluePairs[];
    gasketList: keyvaluePairs[];
    generalList: keyvaluePairs[];
    nutList: keyvaluePairs[];
    weightList: keyvaluePairs[];
    boltFilterList: keyvaluePairs[];
    flangeFilterList: keyvaluePairs[];
    gasketFilterList: keyvaluePairs[];
    generalFilterList: keyvaluePairs[];
    nutFilterList: keyvaluePairs[];
    weightFilterList: keyvaluePairs[];
    toolsDataFilterList: keyvaluePairs[] = [];
    panelOpenState = false;
    //showpanel = false;
    activeTabId: 'kt_quick_panel_logs' | 'kt_quick_panel_notifications' | 'kt_quick_panel_settings' = 'kt_quick_panel_logs';
    private _calcOutput: CalculationResult;

    constructor(
        @Inject(LOCALE_ID) public locale: string,
        private layout: LayoutService,
        private measSvc: MeasurementService,
        private globalEmittersService: GlobalEmittersService
    ) {}

    ngOnInit(): void {
        this.extrasQuickPanelOffcanvasDirectionCSSClass = `offcanvas-${this.layout.getProp('extras.quickPanel.offcanvas.direction')}`;
        this.globalEmittersService.SelectedToolData.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            if (!value) return;

            this.toolsDataFilterList = [];

            const tighteningMethod = this.calcOutput.input.others.tighteningMethod;
            const excludeItems = [
                'manufacturer',
                'warnings',
                'diagramId',
                'imageId',
                'isSelected',
                'isPowered',
                'id',
                'name',
                'tensionerToolType',
                'toolFit',
                'torqueToolType',
                'cover',
            ];

            Object.keys(value).forEach((item) => {
                if (
                    (tighteningMethod === 0 && (item === 'maximumPressure' || item === 'maximumTorque')) ||
                    (tighteningMethod === 1 && !excludeItems.includes(item))
                ) {
                    this.addToolData(item, value[item], tighteningMethod, value);
                }
            });
        });
        this.globalEmittersService.unitUpdated.pipe(takeUntil(this.destroy$)).subscribe((resp) => {
            if (resp) {
                if (this._calcOutput) this.loadAdvancedOutputLists();
            }
        });
    }

    loadAdvancedOutputLists() {
        this.boltList = [];
        this.flangeList = [];
        this.gasketList = [];
        this.nutList = [];
        this.weightList = [];

        if (this.calcOutput !== undefined && this.calcOutput !== null && this.calcOutput?.output !== null) {
            if (this.calcOutput.output.bolt) {
                for (let item of Object.keys(this.calcOutput.output.bolt)) {
                    //In case of Torque tightening procedure, display the below fields as N/A
                    if (this.calcOutput.input.others.tighteningMethod == 0) {
                        switch (item) {
                            case 'appliedBoltYieldPercentage1':
                            case 'appliedBoltYieldPercentage2':
                            case 'appliedBoltYieldPercentage3':
                            case 'appliedBoltYieldPercentage4':
                                this.boltList.push({ key: this.getFormatedName(item), value: 'N/A' });
                                break;
                            default:
                                this.boltList.push({ key: this.getFormatedName(item), value: this.getformatedValue(this.calcOutput.output.bolt[item], item) });
                                break;
                        }
                    } else this.boltList.push({ key: this.getFormatedName(item), value: this.getformatedValue(this.calcOutput.output.bolt[item], item) });
                }
                this.boltFilterList = this.boltList;
            }

            if (this.calcOutput.output.flange) {
                for (let item of Object.keys(this.calcOutput.output.flange)) {
                    this.flangeList.push({ key: this.getFormatedName(item), value: this.getformatedValue(this.calcOutput.output.flange[item], item) });
                }
                this.flangeFilterList = this.flangeList;
            }

            if (this.calcOutput.output.gasket) {
                for (let item of Object.keys(this.calcOutput.output.gasket)) {
                    if (this.calcOutput.input.others.tighteningMethod == 0) {
                        //In case of Torque tightening procedure, display the below fields as N/A
                        switch (item) {
                            case 'appliedGasketStressPercentage':
                                this.gasketList.push({ key: this.getFormatedName(item), value: 'N/A' });
                                break;
                            case 'pAdditional':
                                break;
                            default:
                                this.gasketList.push({
                                    key: this.getFormatedName(item),
                                    value: this.getformatedValue(this.calcOutput.output.gasket[item], item),
                                });
                                break;
                        }
                    } else this.gasketList.push({ key: this.getFormatedName(item), value: this.getformatedValue(this.calcOutput.output.gasket[item], item) });
                }
                this.gasketFilterList = this.gasketList;
            }

            if (this.calcOutput.output.nut) {
                for (let item of Object.keys(this.calcOutput.output.nut)) {
                    this.nutList.push({ key: this.getFormatedName(item), value: this.getformatedValue(this.calcOutput.output.nut[item], item) });
                }
                this.nutFilterList = this.nutList;
            }

            if (this.calcOutput.output.weight) {
                for (let item of Object.keys(this.calcOutput.output.weight)) {
                    this.weightList.push({ key: this.getFormatedName(item), value: this.getformatedValue(this.calcOutput.output.weight[item], item) });
                }
                this.weightFilterList = this.weightList;
            }
        }
    }

    applyFilter(filterData: string) {
        const filterValue = filterData;
        this.boltFilterList = this.boltList.filter(function (element) {
            return element.key.toLowerCase().includes(filterValue);
        });
        this.flangeFilterList = this.flangeList.filter(function (element) {
            return element.key.toLowerCase().includes(filterValue);
        });
        this.gasketFilterList = this.gasketList.filter(function (element) {
            return element.key.toLowerCase().includes(filterValue);
        });
        this.nutFilterList = this.nutList.filter(function (element) {
            return element.key.toLowerCase().includes(filterValue);
        });
        this.weightFilterList = this.weightList.filter(function (element) {
            return element.key.toLowerCase().includes(filterValue);
        });

        this.toolsDataFilterList = this.toolsDataFilterList.filter(function (element) {
            return element.key.toLowerCase().includes(filterValue);
        });
    }

    getFormatedName(name: string): string {
        if (name != null) {
            let formatedName = (name.charAt(0).toUpperCase() + name.slice(1)).match(/[A-Z]+[A-Z]*[a-z]*|[0-9]+/g);
            if (formatedName != null) {
                return formatedName.join(' ');
            }
            return name;
        }
    }

    getformatedValue(item: any, name: string): string {
        if (typeof item === 'object') {
            return this.measSvc.getUserPrefered(item);
        } else if (typeof item === 'number') {
            if (!name.toLocaleLowerCase().includes('percentage')) {
                return formatNumber(item, this.locale, '1.0-2');
            } else {
                return this.measSvc.getFormattedPercentage(item);
            }
        }
        return item;
    }

    setActiveTabId(tabId) {
        this.activeTabId = tabId;
    }

    getActiveCSSClasses(tabId) {
        if (tabId !== this.activeTabId) {
            return '';
        }
        return 'active show';
    }

    closetoggle() {
        this.hidepanel.emit();
    }

    // Helper function to add formatted data to toolsDataFilterList
    addToolData(item, itemValue, tighteningMethod, tool) {
        let value;

        // Determine the value based on the tightening method and tool state
        if (tighteningMethod === 0 && !tool.isPowered) {
            value = 'N/A';
        } else {
            value = this.getformatedValue(itemValue, item);
        }

        // Push the formatted data into the toolsDataFilterList
        this.toolsDataFilterList.push({
            key: this.getFormatedName(item),
            value: value,
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
